<template>
    <div class="expired-lot-inventory-page-wrapper">
        <list-page v-if="!hasFailedRequests"
                   :page-model="pageModel"
                   :title-text="$t('expirationTrackedInventories.list.title')"
                   back-route="/available-inventories"
        >
            <div
                slot="titleAction"
                class="title-action-buttons"
            >
                <title-button-tooltip
                    v-tooltip.bottom="{
                        content: $t('expirationTrackedInventories.disclaimer'),
                        delay: { show: 500, hide: 300 },
                    }"
                />
            </div>
            <template #search="{ filter, filters }">
                <keyword-filter id="filter-name"
                                :column="filters.searchQuery"
                                :value="filters.searchQuery.filter.value"
                                @filter="filter"
                />
            </template>
            <template #filters="{ filter, filters }">
                <flex-row v-if="$mq !== 'mobile'">
                    <div class="col col--no-grow">
                        <select-filter
                            id="expired-inventory-filter-inventoryTypes"
                            :name="filters.inventoryTypes.name"
                            :column="filters.inventoryTypes"
                            :searchable="false"
                            option-label="label"
                            value-field="value"
                            track-by="value"
                            :disabled="failedWarehouse"
                            @filter="filter"
                        >
                            <template>
                                {{ filters.inventoryTypes }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow">
                        <select-filter
                            id="expired-inventory-filter-warhouse"
                            :name="filters.warehouse.name"
                            :column="filters.warehouse"
                            :searchable="false"
                            option-label="label"
                            value-field="value"
                            track-by="value"
                            :disabled="failedWarehouse"
                            @filter="filter"
                        >
                            <template
                                #singleLabel="{ option }"
                            >
                                {{ filters.warehouse.name }}: {{ option.label }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow">
                        <select-filter id="expired-inventory-filter-lotnumber"
                                       :name="filters.lotNumber.name"
                                       :column="filters.lotNumber"
                                       :searchable="false"
                                       option-label="label"
                                       value-field="value"
                                       track-by="value"
                                       @filter="filter"
                        >
                            <template>
                                {{ filters.lotnumber }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow">
                        <select-filter id="expired-inventory-filter-unavailableDate"
                                       name="filters.unavailableDate.name"
                                       :column="filters.unavailableDate"
                                       :searchable="false"
                                       option-label="label"
                                       value-field="value"
                                       track-by="value"
                                       @filter="filter"
                        >
                            <template>
                                {{ filters.unavailableDate }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow">
                        <select-filter id="expired-inventory-filter-expirationDate"
                                       name="filters.expirationDate.name"
                                       :column="filters.expirationDates"
                                       :searchable="false"
                                       option-label="label"
                                       value-field="value"
                                       track-by="value"
                                       @filter="filter"
                        >
                            <template>
                                {{ filters.expirationDate }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--width-date-range">
                        <date-picker id="expired-inventory-filter-expiryDate"
                                     :value.sync="filters.expirationDate.filter.value"
                                     :placeholder="$t('expirationTrackedInventories.labels.expirationDate')"
                                     :min-date="filters.expirationDate.filter.min"
                                     :max-date="filters.expirationDate.filter.max"
                                     mode="range"
                                     filter-style
                                     @change="filterByDate"
                        />
                    </div>
                    <div v-if="isCreateBussinessOrderAllow"
                         class="col col--on-right"
                    >
                        <base-button
                            class="button--no-uppercase"
                            primary
                            @click="createExpiryItemsBusinessOrder()"
                        >
                            {{ $t('expirationTrackedInventories.labels.createBusinessOrder') }}
                        </base-button>
                    </div>
                </flex-row>
            </template>
            <template #notification>
                <base-notification v-if="expiredItemCount"
                                   error
                                   small
                                   bright
                >
                    <p class="notification__title">
                        {{ $t('expirationTrackedInventories.list.approachingExpirationWarning', { count: expiredItemCount }) }}
                    </p>
                    <p v-if="expiredItemCount">
                        <strong>
                            <router-link v-if="$hasAccess('items:read')"
                                         :to="{
                                             name: 'inventory-approaching-expiration-window',
                                             params: { backLink: 'expiration-tracked-inventories' },
                                             query: { dateRange : 30 }
                                         }"
                                         class="underline-text-only"
                            >
                                <span v-t="'expirationTrackedInventories.list.manageExpiredInventory'"
                                      class="underline-text-only__text"
                                />

                                <svg class="underline-text-only__icon"
                                     width="15"
                                     height="15"
                                >
                                    <use xlink:href="#link" />
                                </svg>
                            </router-link>
                        </strong>
                    </p>
                </base-notification>
            </template>
            <base-table v-if="$mq !== 'mobile' || !pageModel.hasMobileView"
                        fixed-header
                        invert-colors
                        offset-sides="lg"
                        :row-uniq-id="pageModel.uniqueField"
                        :columns="pageModel.columns"
                        :rows="pageModel.table.items"
                        :actions="pageModel.actions"
                        :loading="pageModel.table.loading"
            >
                <p slot="noData">
                    {{ $t('expirationTrackedInventories.list.noResults') }}
                </p>
            </base-table>

            <base-pagination
                v-if="hasPages && $mq !== 'mobile'"
                slot="pagination"
                :current-page="pageModel.table.page"
                :results="pageModel.table.total"
                :items-per-page="pageModel.table.pageSize"
                :page-route="currentPageRoute"
                class="list-page__footer"
                @paging="paging"
            />
        </list-page>
        <layout-secondary v-else>
            <failed-to-load @retry="reloadComponent" />
        </layout-secondary>
    </div>
</template>

<script>
import { $t, $d } from '@/translations';
import constants from '@/core/constants';
import { BaseNotification } from '@/components/base';
import { Serializers } from '@/core/table/tableStateUrl';
import expiredItemsApi from '@/services/resources/expireditemsApi';
import formatters from '@/core/table/tableFormatters';
import { BaseButton } from '@/components/buttons';
import { LayoutSecondary } from '@/components/layout';
import { BaseFilterModel, SelectFilterModel, DateRangeFilterModel } from '@/core/table/filterModels';
import {
    BaseTable,
    BasePagination,
    KeywordFilter,
    SelectFilter,
} from '@/components/table';
import DatePicker from '@/components/DatePicker';
import { ListPage, ListPageModel } from '@/components/pages';
import FailedToLoad from '@/components/FailedToLoad';
import ExpiredLotBusinessOrderCreateModal from '@/views/availableInventories/ExpiredLotBusinessOrderCreateModal';
import BusinessOrderViewModal from '@/views/business-orders/BusinessOrderViewModal';
import businessOrdersApi from '@/services/resources/businessOrdersApi';
import TitleButtonTooltip from '@/components/buttons/TitleButtonTooltip';
import approachingExpirationApi from '@/services/resources/approachingExpirationApi';

const filterSerializer = {
    expirationDateFrom: Serializers.date,
    expirationDateTo: Serializers.date,
};

export default {
    components: {
        BaseButton,
        DatePicker,
        BaseNotification,
        TitleButtonTooltip,
        FailedToLoad,
        BaseTable,
        ListPage,
        BasePagination,
        LayoutSecondary,
        KeywordFilter,
        SelectFilter,
    },
    props: {
    },
    data() {
        const listPage = {
            name: $t('expirationTrackedInventories.list.tittle'),
        };

        const skuColumn = {
            name: $t('expirationTrackedInventories.labels.sku'),
            field: 'itemId',
            minWidth: '100px',
        };

        const minDate = new Date();
        minDate.setDate(minDate.getDate() - constants.domain.maxOrderSearchDays);
        const expirationDateColumn = {
            name: $t('expirationTrackedInventories.labels.expirationDate'),
            field: 'expirationDate',
            format: formatters.date('short', false),
            minWidth: '14%',
            filter: new DateRangeFilterModel(null, 'expirationDateFrom', 'expirationDateTo', {
                max: new Date(),
                min: minDate,
            }),
        };

        const warehouseColumn = {
            name: $t('expirationTrackedInventories.labels.warehouse'),
            field: 'warehouse',
            minWidth: '16%',
        };

        const lotNumberColumn = {
            name: $t('expirationTrackedInventories.labels.lotNumber'),
            field: 'lotNumber',
            minWidth: '16%',
        };

        const inventoryTypeColumn = {
            name: $t('expirationTrackedInventories.labels.inventoryType'),
            field: 'inventoryType',
            minWidth: '16%',
            filter: new SelectFilterModel(),
        };

        const unavailableDateColumn = {
            name: $t('expirationTrackedInventories.labels.unavailableDate'),
            field: 'unavailableDate',
            format: formatters.date('short', false),
            minWidth: '14%',
            filter: new SelectFilterModel(),
        };

        const filters = {
            lotNumber: {
                name: $t('expirationTrackedInventories.labels.lotNumber'),
                field: 'lotNumber',
                filter: new SelectFilterModel(),
            },
            expirationDate: expirationDateColumn,
            expirationDateFrom: expirationDateColumn,
            expirationDateTo: expirationDateColumn,
            searchQuery: {
                field: 'searchQuery',
                placeholder: $t('expirationTrackedInventories.filters.searchPlaceholder'),
                tag: $t('general.filters.search'),
                filter: new BaseFilterModel(''),
            },
            warehouse: {
                name: $t('expirationTrackedInventories.labels.warehouse'),
                field: 'warehouse',
                filter: new SelectFilterModel(),
            },
            inventoryType: inventoryTypeColumn,
            unavailableDate: unavailableDateColumn,
        };

        const columns = [
            skuColumn,
            {
                name: $t('expirationTrackedInventories.labels.description'),
                field: 'description',
                minWidth: '16%',
            },
            {
                name: $t('expirationTrackedInventories.labels.expirationWindow'),
                field: 'expirationWindow',
                minWidth: '12%',
            },
            unavailableDateColumn,
            expirationDateColumn,
            warehouseColumn,
            lotNumberColumn,
            {
                name: $t('expirationTrackedInventories.labels.quantity'),
                field: 'quantity',
                minWidth: '14%',
            },
            {
                name: $t('expirationTrackedInventories.labels.orderId'),
                field: 'expiredItemsOrderId',
                minWidth: '14%',
                actionDataFields: ['expiredItemsOrderId'],
                action: data => this.showDetails(data),
            },
            {
                name: $t('expirationTrackedInventories.labels.orderStatus'),
                field: 'expiredItemsOrderStatusDescription',
                minWidth: '14%',
            },
        ];

        const pageModel = new ListPageModel(expiredItemsApi, {
            router: this.$router,
            serializers: filterSerializer,
        }, {
            uniqueField: 'id',
            filters,
            columns,
            hasMobileView: true,
        }, {
            defaultPageSize: 10,
            itemsField: 'expiredItems',
        });

        return {
            pageModel,
            filters,
            listPage,
            baseColumns: columns,
            baseWarehouseColumns: [],
            mobileModalOpened: false,
            openPreviousModal: false,
            modalData: {
                modalName: null,
                expiredItems: null,
            },
            failedWarehouse: false,
            warehouses: [],
            isFilterLoaded: false,
            expiredItemCount: 0,
        };
    },
    computed: {
        hasPages() {
            return this.pageModel.hasPages();
        },
        hasFailedRequests() {
            return this.pageModel.hasFailedRequests();
        },
        isTabletSize() {
            return this.$mq === 'tablet' || this.$mq === 'mobile';
        },
        showMobileModal() {
            return this.mobileModalOpened && this.isTabletSize;
        },
        currentPageRoute() {
            return this.$route.path;
        },
        isCreateBussinessOrderAllow() {
            return this.isFilterLoaded;
        },
    },
    watch: {
        $mq(value) {
            this.pageModel.updateMobileView(value === 'mobile');
        },
        hasFailedRequests(value) {
            if (value) {
                const error = this.pageModel.getError();

                this.$toast.error(error);
            }
        },
    },
    async mounted() {
        await this.loadFilters();
        await this.pageModel.restore();
        const response = await approachingExpirationApi.getAll();
        this.expiredItemCount = response.totalSkuCount;
    },
    methods: {
        async loadFilters() {
            try {
                const response = await expiredItemsApi.getFilters();
                const inventoryTypes = [{ filter: 'Expired', value: 1 }, { filter: 'Unavailable', value: 2 }];
                await this.loadWarehouseFilter(response.warehouses);
                await this.loadLotNumberFilter(response.expiredItemLots);
                await this.loadExpirationDateFilter(response.expirationDates);
                this.isFilterLoaded = response.warehouses.length > 0 || response.expiredItemLots.length > 0;
                await this.loadFilterTypeFilter(inventoryTypes);
                await this.loadUnavailableDateFilter(response.unavailableDates);
            } catch (error) {
                this.$toast.error(error);
                this.filtersRetrievalFailed = true;
            }
        },
        formatDate(value) {
            return $d(new Date(value), 'short');
        },
        async loadWarehouseFilter(warehouses) {
            this.warehouses = [...warehouses];
            this.failedWarehouse = false;
            try {
                this.pageModel.filters.warehouse = {
                    name: $t('expirationTrackedInventories.labels.warehouse'),
                    field: 'warehouse',
                    filter: new SelectFilterModel(
                        null,
                        warehouses ? warehouses.map(x => ({
                            label: x.warehouseName,
                            value: x.warehouseId,
                        })) : [],
                    ),
                };
            } catch (e) {
                this.$toast.error(e);
                this.failedWarehouse = true;
            }
        },
        async loadLotNumberFilter(lots) {
            this.pageModel.filters.lotNumber = {
                name: $t('expirationTrackedInventories.labels.lotNumber'),
                field: 'lotNumber',
                filter: new SelectFilterModel(
                    null,
                    lots ? lots.map(x => ({
                        label: x,
                        value: x,
                    })) : [],
                ),
            };
        },
        async loadExpirationDateFilter(expirationDates) {
            this.pageModel.filters.expirationDates = {
                name: $t('expirationTrackedInventories.labels.expirationDate'),
                field: 'expirationDate',
                filter: new SelectFilterModel(
                    null,
                    expirationDates ? expirationDates.map(x => ({
                        label: this.formatDate(x),
                        value: x,
                    })) : [],
                ),
            };
        },
        async loadFilterTypeFilter(inventoryTypes) {
            this.pageModel.filters.inventoryTypes = {
                name: $t('expirationTrackedInventories.labels.inventoryType'),
                field: 'inventoryType',
                filter: new SelectFilterModel(
                    null,
                    inventoryTypes ? inventoryTypes.map(x => ({
                        label: x.filter,
                        value: x.value,
                    })) : [],
                ),
            };
        },
        async loadUnavailableDateFilter(unavailableDates) {
            this.pageModel.filters.unavailableDate = {
                name: $t('expirationTrackedInventories.labels.unavailableDate'),
                field: 'unavailableDate',
                filter: new SelectFilterModel(
                    null,
                    unavailableDates ? unavailableDates.map(x => ({
                        label: this.formatDate(x),
                        value: x,
                    })) : [],
                ),
            };
        },
        async showDetails(data) {
            if (data === '-') return null;
            const businessOrder = await this.fetchOrder(data.expiredItemsOrderId);
            return businessOrder && this.showDetailsModal(businessOrder);
        },
        async showDetailsModal(businessOrder) {
            this.modalData = {
                modalName: 'BusinessOrderViewModal',
                businessOrder,
            };
            if (!this.isTabletSize) {
                this.$modal.show(BusinessOrderViewModal, { businessOrder }, {
                    name: 'businessOrderPreview',
                    height: 'auto',
                    width: 720,
                    adaptive: true,
                    scrollable: true,
                    clickToClose: false,
                }, {},
                {
                    save: () => this.refreshTable(),
                });
            } else {
                this.mobileModalOpened = true;
            }
        },
        async createExpiryItemsBusinessOrder() {
            const response = await expiredItemsApi.expireItemsToCreateOrder();
            if (!this.isTabletSize) {
                this.$modal.show(ExpiredLotBusinessOrderCreateModal, {
                    expiredItems: response,
                    warehouses: this.warehouses,
                    isSmartFreightSelected: true,
                }, {
                    name: 'expiredLotBusinessOrderCreateModal',
                    height: 'auto',
                    width: 720,
                    adaptive: true,
                    scrollable: true,
                    clickToClose: false,
                },
                {
                    save: () => this.reloadComponent(),
                });
            } else {
                this.mobileModalOpened = true;
            }
        },
        async fetchOrder(id) {
            return this.$monitor(() => businessOrdersApi.get(id));
        },
        filter(field, value) {
            this.pageModel.filter(field, value);
        },
        refreshTable() {
            return this.pageModel.refresh();
        },
        reloadComponent() {
            this.loadFilters();
            this.refreshTable();
        },
        closeModal() {
            this.mobileModalOpened = false;
            this.openPreviousModal = false;
        },
        filterByDate() {
            const { filter } = this.filters.expirationDate;
            this.pageModel.filter(filter.getFilterValue());
        },
        paging(data) {
            this.pageModel.paging(data);
        },
    },
};
</script>

<style scoped lang="scss">
.expired-lot-inventory-page-wrapper {
    height: 100%;

    .search-container {
        position: absolute;
        top: 75px;
        right: 20px;
        width: 360px;

        ::v-deep .search {
            ::placeholder {
                color: $color-gray-400;
            }

            &__input,
            &__icon {
                color: $color-gray-200;
            }
        }

        @media screen and (max-width: $media-mobile-nav) {
            top: 25px;
        }
    }

    .filters-container {
        margin: 0 -50px;
        padding: 20px;
        background-color: $color-teal-920;

        ::v-deep .vc-popover-content-wrapper {
            z-index: 51;
        }

        .manage-edi-button {
            margin-left: 20px;
        }

        .header-buttons {
            margin-right: 0;
            margin-left: auto;

            .edit-allocation-button {
                margin-right: 20px;
            }
        }
    }
}

.title-action-buttons {
    display: flex;
}

.mobile-view {
    margin: 0 (-$page-padding-sides);
}
</style>
